/**
 * The ICS-201 card's header actions, as the sibling cards render theirs.
 *
 * The shadcn counterpart to `GenerateIcs201Button`, which stays where it is for
 * the Legacy Initial Response briefing. This captures Box 4 at the live view
 * size and sends those PNG bytes as `mapSketch`, so the PDF Map field is the
 * framed sketch rather than a server refetch of a stored blob.
 */

import React from 'react';
import { EyeIcon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useDownloadFile from 'components/FileManagement/hooks/useDownloadFile';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useIcs201Op0 } from 'hooks/incident/useIcs201Op0';

import { useActualIncidentId } from '../../../hooks/commonHooks';
import { useIcsFormPdfPreview } from '../../../context/IcsFormPdfPreviewContext';
import { useIcsFormFamily } from '../../../hooks/useIcsFormFamily';
import { resolveIcs201MapSketchFile } from './ics201MapSketch';

type Ics201ExportActionsProps = {
  /** Live Box 4 screenshot. Undefined if the map is not ready. */
  captureSketchFile?: () => Promise<File | undefined> | undefined;
};

export const Ics201ExportActions: React.FC<Ics201ExportActionsProps> = ({
  captureSketchFile,
}) => {
  const { previewFromSignedUrl, exportAsForm, isExporting } =
    useIcsFormPdfPreview();
  const formFamily = useIcsFormFamily();
  const incidentId = useActualIncidentId();
  const { data: incidentInfo } = useIncidentInfo();
  const downloadFile = useDownloadFile();
  const { existing201File, displayDocuments } = useIcs201Op0(incidentId);
  const { mutateAsync: getSignedUrl, isPending: isLoadingUrl } = useSignedUrl();

  const generateAndPreview = async (format: 'pdf' | 'docx' = 'pdf') => {
    const mapSketch = await resolveIcs201MapSketchFile({
      captureSketchFile,
      mapSketchUrl: incidentInfo?.mapSketchUrl,
      getSignedUrl,
    });

    exportAsForm({
      formname: 'ICS_201-CG',
      data: {},
      mapSketch,
      periodNumber: 0,
      format,
    });
  };

  const previewExisting = async () => {
    if (!existing201File) return;
    try {
      const signedUrl = await getSignedUrl(existing201File.fileUrl);
      previewFromSignedUrl({
        fileUrl: signedUrl,
        fileName: existing201File.file_name,
        onDownload: () => {
          void downloadFile(existing201File);
        },
      });
    } catch {
      toast.error('Failed to load preview');
    }
  };

  const busy = isExporting || isLoadingUrl;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            aria-label="Preview or export ICS-201"
            data-testid="ics201-export-actions"
            data-tour="ics201-export"
          />
        }
      >
        <EyeIcon /> Preview ICS-201
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="dark">
        {formFamily !== 'fema' ? (
          <DropdownMenuItem
            disabled={busy}
            onClick={() => void generateAndPreview('pdf')}
          >
            Generate / Export PDF
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          disabled={busy}
          onClick={() => void generateAndPreview('docx')}
        >
          Generate / Export Word
        </DropdownMenuItem>
        {displayDocuments ? (
          <DropdownMenuItem
            disabled={busy}
            onClick={() => void previewExisting()}
          >
            Open last generated ICS-201
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default Ics201ExportActions;
